import type { TRunQuery } from './api';

// Board 09's switch. "Problems" is the run's own status — the worst copy that was not
// recovered — so a run whose error branch saved it does not count as one.
export type TRunShow = 'all' | 'problems' | 'progress' | 'stopped';

export const RUN_SHOW: readonly { value: TRunShow; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'problems', label: 'Problems' },
  { value: 'progress', label: 'In progress' },
  { value: 'stopped', label: 'Stopped' },
];

// `failing`: still running, an event retrying after a failed try — a problem before it gives up.
const SHOW_STATUS: Record<Exclude<TRunShow, 'all'>, NonNullable<TRunQuery['status']>> = {
  problems: ['failed', 'error', 'failing'],
  progress: ['running'],
  stopped: ['filtered', 'duplicate', 'stopped'],
};

export const RUN_PERIODS: readonly { minutes: number; label: string }[] = [
  { minutes: 60, label: 'Last hour' },
  { minutes: 6 * 60, label: 'Last 6 hours' },
  { minutes: 24 * 60, label: 'Last 24 hours' },
  { minutes: 7 * 24 * 60, label: 'Last 7 days' },
  { minutes: 30 * 24 * 60, label: 'Last 30 days' },
];

export const DEFAULT_RUN_MINUTES = 24 * 60;

// Always present, so links built as `{ ...EMPTY_RUN_FILTERS, sourceId: [id] }` stay valid.
const BASE_KEYS = ['flowId', 'nodeKey', 'lane', 'sourceId', 'issueId'] as const;

const EXTRA_KEYS = [
  'destinationId',
  'eventType',
  'customerId',
  'responseStatus',
  'responseStatusClass',
  'errorClass',
  'attempts',
  'origin',
  'authResult',
  'triggerKind',
  'copyStatus',
  'bookmarked',
] as const;

export type TRunFacetField = (typeof BASE_KEYS)[number] | (typeof EXTRA_KEYS)[number];

export interface IRunFilters extends Partial<Record<(typeof EXTRA_KEYS)[number], string[]>> {
  minutes: number;
  show: TRunShow;
  flowId: string[];
  nodeKey: string[];
  lane: string[];
  sourceId: string[];
  issueId: string[];
  q?: string;
  /** The events search language, over the trigger request (docs/event-search-language.md). */
  search?: string;
  /** A window pinned on the chart; both or neither, and they win over `minutes`. */
  from?: string;
  to?: string;
  agent?: true;
  agentEnd?: 'limit';
}

export const runFacetValues = (filters: IRunFilters, field: TRunFacetField): string[] =>
  filters[field] ?? [];

export const withRunFacet = (
  filters: IRunFilters,
  field: TRunFacetField,
  values: string[],
): IRunFilters => ({ ...filters, [field]: values });

/** Every facet `/v1/runs/facets` counts; an issue is reached from the issue, not picked here. */
export type TRunCountedField = Exclude<TRunFacetField, 'issueId'>;

export const RUN_FACET_GROUPS: readonly {
  title: string;
  facets: readonly { field: TRunCountedField; label: string }[];
}[] = [
  {
    title: 'Route',
    facets: [
      { field: 'flowId', label: 'Flow' },
      { field: 'sourceId', label: 'Source' },
      { field: 'destinationId', label: 'Destination' },
      { field: 'customerId', label: 'Customer' },
      { field: 'nodeKey', label: 'Step' },
      { field: 'lane', label: 'Branch' },
    ],
  },
  {
    title: 'Outcome',
    facets: [
      { field: 'copyStatus', label: 'Event status' },
      { field: 'responseStatus', label: 'Response code' },
      { field: 'responseStatusClass', label: 'Response class' },
      { field: 'errorClass', label: 'Error class' },
      { field: 'attempts', label: 'Attempts' },
    ],
  },
  {
    title: 'Identity',
    facets: [
      { field: 'eventType', label: 'Event type' },
      { field: 'origin', label: 'Origin' },
      { field: 'authResult', label: 'Sender check' },
      { field: 'triggerKind', label: 'Started by' },
      { field: 'bookmarked', label: 'Bookmarked' },
    ],
  },
];

export const RUN_FACET_LABEL: Record<TRunFacetField, string> = {
  issueId: 'Issue',
  ...(Object.fromEntries(
    RUN_FACET_GROUPS.flatMap((group) => group.facets).map((facet) => [facet.field, facet.label]),
  ) as Record<TRunCountedField, string>),
};

// Words where the value is an enum; ids are named by the facet's own `label`.
export const RUN_FACET_WORD: Partial<Record<TRunFacetField, Record<string, string>>> = {
  origin: {
    trigger: 'Sent to us',
    replay: 'Replayed by hand',
    recover: 'Recovered',
    agent: 'Run by an agent',
  },
  triggerKind: { webhook: 'A request', schedule: 'A schedule', manual: 'Run now' },
  bookmarked: { true: 'Bookmarked', false: 'Not bookmarked' },
  copyStatus: {
    running: 'Running',
    waiting: 'Waiting',
    retrying: 'Retrying',
    paused: 'Paused',
    on_hold: 'On hold',
    completed: 'Completed',
    failed: 'Failed',
    error: 'Error',
    filtered: 'Stopped by a filter',
    duplicate: 'Duplicate',
    stopped: 'Stopped',
    merged: 'Merged',
  },
};

export const EMPTY_RUN_FILTERS: IRunFilters = {
  minutes: DEFAULT_RUN_MINUTES,
  show: 'all',
  flowId: [],
  nodeKey: [],
  lane: [],
  sourceId: [],
  issueId: [],
};

const list = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string' && raw.length > 0) return raw.split(',');
  return [];
};

export const parseRunFilters = (search: Record<string, unknown>): IRunFilters => {
  const minutes = Number(search.minutes);
  const show = RUN_SHOW.find((option) => option.value === search.show)?.value ?? 'all';
  return {
    minutes: Number.isFinite(minutes) && minutes > 0 ? minutes : DEFAULT_RUN_MINUTES,
    show,
    flowId: list(search.flowId),
    nodeKey: list(search.nodeKey),
    lane: list(search.lane),
    sourceId: list(search.sourceId),
    issueId: list(search.issueId),
    ...Object.fromEntries(
      EXTRA_KEYS.map((key) => [key, list(search[key])]).filter(([, values]) => values.length > 0),
    ),
    q: typeof search.q === 'string' && search.q.trim() ? search.q.trim() : undefined,
    search:
      typeof search.search === 'string' && search.search.trim() ? search.search.trim() : undefined,
    ...pinnedWindow(search.from, search.to),
    ...(search.agent === '1' || search.agent === 1 || search.agent === true
      ? { agent: true as const }
      : {}),
    ...(search.agentEnd === 'limit' ? { agentEnd: 'limit' as const } : {}),
  };
};

const pinnedWindow = (from: unknown, to: unknown): { from?: string; to?: string } => {
  if (typeof from !== 'string' || typeof to !== 'string') return {};
  const start = Date.parse(from);
  const end = Date.parse(to);
  return Number.isFinite(start) && Number.isFinite(end) && start < end ? { from, to } : {};
};

export const runFiltersToSearch = (filters: IRunFilters): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  if (filters.minutes !== DEFAULT_RUN_MINUTES) out.minutes = filters.minutes;
  if (filters.show !== 'all') out.show = filters.show;
  for (const key of [...BASE_KEYS, ...EXTRA_KEYS]) {
    const values = filters[key] ?? [];
    if (values.length > 0) out[key] = values.join(',');
  }
  if (filters.q) out.q = filters.q;
  if (filters.search) out.search = filters.search;
  if (filters.from && filters.to) {
    out.from = filters.from;
    out.to = filters.to;
  }
  if (filters.agent) out.agent = 1;
  if (filters.agentEnd) out.agentEnd = filters.agentEnd;
  return out;
};

export const runFiltersActive = (filters: IRunFilters): boolean =>
  filters.show !== 'all' ||
  Boolean(filters.agent) ||
  Boolean(filters.agentEnd) ||
  Boolean(filters.q) ||
  Boolean(filters.search) ||
  [...BASE_KEYS, ...EXTRA_KEYS].some((key) => (filters[key] ?? []).length > 0);

export type TTraceTab = 'in' | 'out' | 'changes' | 'attempts';

export interface ITraceSearch {
  at?: string;
  copy?: string;
  step?: string;
  tab?: TTraceTab;
  view?: 'agent';
  rec?: number;
}

const TRACE_TABS: readonly TTraceTab[] = ['in', 'out', 'changes', 'attempts'];

export const parseTraceSearch = (search: Record<string, unknown>): ITraceSearch => {
  const text = (key: string) =>
    typeof search[key] === 'string' && search[key] ? (search[key] as string) : undefined;
  const tab = text('tab');
  return {
    at: text('at'),
    copy: text('copy'),
    step: text('step'),
    tab: TRACE_TABS.find((value) => value === tab),
    view: search.view === 'agent' ? 'agent' : undefined,
    rec:
      Number.isInteger(Number(search.rec)) && Number(search.rec) > 0
        ? Number(search.rec)
        : undefined,
  };
};

type TEnumKey =
  'responseStatusClass' | 'attempts' | 'origin' | 'authResult' | 'triggerKind' | 'copyStatus';
type TEnumOf<K extends TEnumKey> = NonNullable<TRunQuery[K]>[number];

const ENUMS: { [K in TEnumKey]: readonly TEnumOf<K>[] } = {
  responseStatusClass: ['2xx', '3xx', '4xx', '5xx'],
  attempts: ['none', 'first', 'retrying', 'exhausted'],
  origin: ['trigger', 'replay', 'recover', 'agent'],
  authResult: ['verified', 'failed', 'skipped'],
  triggerKind: ['webhook', 'schedule', 'manual'],
  copyStatus: [
    'running',
    'waiting',
    'retrying',
    'paused',
    'on_hold',
    'completed',
    'failed',
    'error',
    'filtered',
    'duplicate',
    'stopped',
    'merged',
  ],
};

// A hand-edited URL can carry a word the API refuses with a 400; it is dropped instead.
const known = <K extends TEnumKey>(
  key: K,
  values: string[] | undefined,
): TEnumOf<K>[] | undefined => {
  const allowed: readonly string[] = ENUMS[key];
  const kept = (values ?? []).filter((value): value is TEnumOf<K> => allowed.includes(value));
  return kept.length > 0 ? kept : undefined;
};

export const runQuery = (filters: IRunFilters, windowEnd: number): TRunQuery => {
  const pinned = filters.from && filters.to && !filters.q;
  const query: TRunQuery = pinned
    ? { from: filters.from, to: filters.to }
    : {
        from: new Date(windowEnd - filters.minutes * 60_000).toISOString(),
        to: new Date(windowEnd).toISOString(),
      };
  if (filters.show !== 'all') query.status = SHOW_STATUS[filters.show];
  if (filters.flowId.length > 0) query.flowId = filters.flowId;
  if (filters.nodeKey.length > 0) query.nodeKey = filters.nodeKey;
  if (filters.lane.length > 0) query.lane = filters.lane;
  if (filters.sourceId.length > 0) query.sourceId = filters.sourceId;
  if (filters.issueId.length > 0) query.issueId = filters.issueId;
  const extra = (key: (typeof EXTRA_KEYS)[number]) => {
    const values = filters[key] ?? [];
    return values.length > 0 ? values : undefined;
  };
  query.destinationId = extra('destinationId');
  query.eventType = extra('eventType');
  query.customerId = extra('customerId');
  query.responseStatus = extra('responseStatus')?.map(Number);
  query.responseStatusClass = known('responseStatusClass', extra('responseStatusClass'));
  query.errorClass = extra('errorClass');
  query.attempts = known('attempts', extra('attempts'));
  query.origin = known('origin', extra('origin'));
  query.authResult = known('authResult', extra('authResult'));
  query.triggerKind = known('triggerKind', extra('triggerKind'));
  query.copyStatus = known('copyStatus', extra('copyStatus'));
  // Both values picked means either, which is no filter at all.
  const bookmarked = extra('bookmarked');
  if (bookmarked?.length === 1) query.bookmarked = bookmarked[0] === 'true';
  if (filters.search) query.search = filters.search;
  if (filters.agent) query.agent = true;
  if (filters.agentEnd) query.agentEnd = [filters.agentEnd];
  for (const key of Object.keys(query) as (keyof TRunQuery)[]) {
    if (query[key] === undefined) delete query[key];
  }
  // An id jumps to its run across the widest window the API takes (31 days, exclusive), not
  // only the picked period — a run from last week would otherwise read as "no such id".
  if (filters.q) {
    query.q = filters.q;
    query.from = new Date(windowEnd - 30 * 24 * 60 * 60_000).toISOString();
  }
  return query;
};
